import type { ReactElement } from 'react';
import { Fragment } from 'react';
import reactStringReplace from 'react-string-replace';

import type { TraderInputsContextValue } from 'app/components/MatchScoreboard/TraderInputsContext/types';

import type {
  DurationTraderInput,
  GetNameFunctionArguments,
  MicromarketConfig,
  PendingTraderInputComponentProps
} from './micromarkets.config';
import { MICROMARKETS, VIDEOGAME_WITH_MICROBETTING } from './micromarkets.config';

const isVideogameWithMicromarkets = (videogame: VideogameSlug) =>
  VIDEOGAME_WITH_MICROBETTING.includes(videogame);

const getMicroMarketTraderInput = (
  microMarket: MicroMarket,
  traderInputs: TraderInputItem[],
  lifecycle: 'staged' | 'pending'
): TraderInputItem | undefined => {
  const traderInputConfig: MicromarketConfig['traderInput'] =
    MICROMARKETS[microMarket.template]?.traderInput;

  return traderInputs.find(
    traderInput =>
      traderInput.event_id === microMarket.event_id &&
      traderInputConfig.category === traderInput.category &&
      traderInputConfig[lifecycle === 'staged' ? 'stagedType' : 'pendingType'] === traderInput.type
  );
};

const selectTraderInput = (
  pushUpdate: TraderInputsContextValue['pushUpdate'],
  market: Market,
  marketTraderInputConfig: MicromarketConfig['traderInput'],
  value: null | string | number | DurationTraderInput
) => {
  if (marketTraderInputConfig.pendingType) {
    pushUpdate({
      category: marketTraderInputConfig.category ?? '',
      type: marketTraderInputConfig.pendingType,
      event_id: market.event_id,
      event_type: market.event_type,
      value
    });
  }
};

const getValue = (
  withTraderInput: boolean | undefined,
  market: GetNameFunctionArguments['market'],
  traderInput: TraderInputItem | undefined
): number | DurationTraderInput | null => {
  if (withTraderInput) {
    return traderInput?.value;
  }

  if (market.line) {
    if (market.line.includes('-')) {
      const values = market.line.split('-');
      return { start: parseInt(values[0]), end: parseInt(values[1]) };
    }

    return parseInt(market.line);
  }

  return null;
};

const formatName = (
  name: string,
  { disabled, market, pushUpdate, traderInputs, traderInputType }: GetNameFunctionArguments,
  getMarketFormatNameComponent: (
    props: PendingTraderInputComponentProps
  ) => ReactElement | null | undefined
): ReactElement | string => {
  const formattedName = name;

  const marketConfig = MICROMARKETS[market.template].traderInput;
  const traderInput = getMicroMarketTraderInput(market, traderInputs, 'pending');

  const props: PendingTraderInputComponentProps = {
    disabled,
    onChange: val => selectTraderInput(pushUpdate, market, marketConfig, val),
    value: getValue(
      traderInputType !== 'bookmaker_display' && traderInputType !== undefined,
      market,
      traderInput
    ),
    onlyLabel: traderInputType !== 'input'
  };

  const textToReplaceRegex = marketConfig.textToReplace
    ? new RegExp(`(${marketConfig.textToReplace.map(val => `\\[${val}\\]`).join('|')})`, 'g')
    : new RegExp(`(\\[${'Line'}\\])`, 'g');

  return (
    <>
      {reactStringReplace(formattedName, textToReplaceRegex, (match, index) => {
        const marketNameComponent = getMarketFormatNameComponent({
          ...props,
          replacedTextMatch: match
        });
        return <Fragment key={index}>{marketNameComponent}</Fragment>;
      })}
    </>
  );
};

const getMarketFormatNameFunction = (
  args: GetNameFunctionArguments
): ((name: string) => ReactElement | string) | undefined => {
  const getMarketFormatNameComponent = MICROMARKETS[args.market.template]?.getFormatNameComponent;
  if (getMarketFormatNameComponent) {
    return name => formatName(name, args, getMarketFormatNameComponent);
  }

  return undefined;
};

export { isVideogameWithMicromarkets, getMarketFormatNameFunction, getMicroMarketTraderInput };
